import { supabase } from '../config/supabase';
import { AnalyticsService } from './AnalyticsService';
import { AnalyticsConsentService } from './AnalyticsConsentService';
import { GoogleAnalyticsService } from './GoogleAnalyticsService';
import { PostHogService } from './PostHogService';

export type AnalyticsDomain = 'product' | 'landing' | 'blog' | 'newsletter' | 'payments' | 'ops';

export interface CanonicalAnalyticsEvent {
  domain: AnalyticsDomain;
  eventName: string;
  userId?: string | null;
  visitorId?: string | null;
  sessionId?: string | null;
  pagePath?: string | null;
  locale?: 'it' | 'en' | null;
  source?: string | null;
  referrer?: string | null;
  properties?: Record<string, unknown>;
  occurredAt?: string;
}

const SESSION_KEY = 'ts_unified_analytics_session_id';

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export class UnifiedAnalyticsService {
  private static bootstrapped = false;

  static bootstrap(): void {
    if (this.bootstrapped) return;
    GoogleAnalyticsService.bootstrap();
    PostHogService.bootstrap();
    this.ensureSessionId();
    this.bootstrapped = true;
  }

  static identifyUser(userId: string | null, traits?: Record<string, unknown>): void {
    this.bootstrap();
    if (!userId) return;
    PostHogService.identify(userId, traits);
  }

  static reset(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SESSION_KEY);
    }
    PostHogService.reset();
    this.bootstrapped = false;
  }

  static getVisitorId(): string {
    return AnalyticsService.getOrCreateVisitorId();
  }

  static ensureSessionId(): string {
    if (typeof sessionStorage === 'undefined') {
      return createSessionId();
    }

    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = createSessionId();
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }

    return sessionId;
  }

  static async captureEvent(event: CanonicalAnalyticsEvent): Promise<void> {
    const isAnonymousPublicEvent =
      !event.userId &&
      (event.domain === 'landing' || event.domain === 'blog');

    if (isAnonymousPublicEvent && !AnalyticsConsentService.hasGranted()) {
      return;
    }

    this.bootstrap();

    const visitorId = event.visitorId || this.getVisitorId();
    const sessionId = event.sessionId || this.ensureSessionId();
    const pagePath = event.pagePath || (typeof window !== 'undefined' ? window.location.pathname : null);
    const referrer = event.referrer ?? (typeof document !== 'undefined' ? document.referrer || null : null);
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || null : null;
    const locale = event.locale || null;
    const properties = event.properties || {};

    try {
      await supabase.rpc('track_analytics_event', {
        p_domain: event.domain,
        p_event_name: event.eventName,
        p_user_id: event.userId || null,
        p_visitor_id: visitorId,
        p_session_id: sessionId,
        p_page_path: pagePath,
        p_locale: locale,
        p_source: event.source || null,
        p_referrer: referrer,
        p_user_agent: userAgent,
        p_properties: properties,
        p_occurred_at: event.occurredAt || new Date().toISOString()
      });
    } catch (error) {
      console.error('Unified analytics Supabase error:', error);
    }

    PostHogService.capture(`${event.domain}_${event.eventName}`, {
      domain: event.domain,
      event_name: event.eventName,
      user_id: event.userId || undefined,
      visitor_id: visitorId,
      session_id: sessionId,
      page_path: pagePath || undefined,
      locale: locale || undefined,
      source: event.source || undefined,
      ...properties
    });

    GoogleAnalyticsService.trackEvent(`${event.domain}_${event.eventName}`, {
      event_category: event.domain,
      page_path: pagePath || undefined,
      locale: locale || undefined,
      source: event.source || undefined
    });
  }

  static async trackLandingPageViewed(params: {
    visitorId?: string | null;
    source?: string | null;
  } = {}): Promise<void> {
    await this.captureEvent({
      domain: 'landing',
      eventName: 'landing_page_viewed',
      visitorId: params.visitorId || null,
      source: params.source || 'landing_page',
      pagePath: '/'
    });
  }

  static async trackLandingCtaClick(params: {
    visitorId?: string | null;
    position: 'hero' | 'footer' | 'header' | 'pricing' | 'platforms';
    target?: 'login' | 'platform' | 'newsletter';
    source?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'landing',
      eventName: 'cta_clicked',
      visitorId: params.visitorId || null,
      source: params.source || `landing_${params.position}`,
      pagePath: '/',
      properties: {
        position: params.position,
        target: params.target || 'login'
      }
    });
  }

  static async trackLandingConversion(params: {
    userId?: string | null;
    visitorId?: string | null;
    source?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'landing',
      eventName: 'signup_completed',
      userId: params.userId || null,
      visitorId: params.visitorId || null,
      source: params.source || 'landing_signup',
      pagePath: '/login',
      properties: {
        conversionSource: 'landing'
      }
    });
  }

  static async trackAuthEvent(
    eventName: 'signup_started' | 'signup_completed' | 'login_completed',
    params: {
      userId?: string | null;
      visitorId?: string | null;
      source?: string | null;
      pagePath?: string | null;
      properties?: Record<string, unknown>;
    } = {}
  ): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName,
      userId: params.userId || null,
      visitorId: params.visitorId || null,
      source: params.source || 'auth',
      pagePath: params.pagePath || '/login',
      properties: params.properties || {}
    });
  }

  static async trackProductPageViewed(params: {
    userId?: string | null;
    pagePath: string;
    pageKey: string;
    source?: string | null;
  }): Promise<void> {
    const eventMap: Record<string, string> = {
      '/report': 'report_viewed',
      '/diary': 'diary_viewed'
    };

    await this.captureEvent({
      domain: 'product',
      eventName: eventMap[params.pagePath] || 'page_viewed',
      userId: params.userId || null,
      source: params.source || 'app_navigation',
      pagePath: params.pagePath,
      properties: {
        pageKey: params.pageKey
      }
    });
  }

  static async trackReportGenerated(params: {
    userId: string;
    source?: string | null;
    reportType?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName: 'report_generated',
      userId: params.userId,
      source: params.source || 'report_builder',
      pagePath: '/report',
      properties: {
        reportType: params.reportType || null
      }
    });
  }

  static async trackReportAiSummaryRequested(params: {
    userId?: string | null;
    source?: string | null;
    provider?: string | null;
    context?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName: 'report_ai_summary_requested',
      userId: params.userId || null,
      source: params.source || 'report_ai',
      pagePath: '/report',
      properties: {
        provider: params.provider || null,
        context: params.context || null
      }
    });
  }

  static async trackDiaryEntryCreated(params: {
    userId: string;
    source?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName: 'diary_entry_created',
      userId: params.userId,
      source: params.source || 'diary_editor',
      pagePath: '/diary'
    });
  }

  static async trackDataFileUploaded(params: {
    userId?: string | null;
    tradeCount?: number;
    source?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName: 'data_file_uploaded',
      userId: params.userId || null,
      source: params.source || 'data_import',
      pagePath: '/data',
      properties: {
        tradeCount: params.tradeCount || 0
      }
    });
  }

  static async trackDataImportCompleted(params: {
    userId?: string | null;
    tradeCount?: number;
    source?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName: 'data_import_completed',
      userId: params.userId || null,
      source: params.source || 'data_import',
      pagePath: '/data',
      properties: {
        tradeCount: params.tradeCount || 0
      }
    });
  }

  static async trackFeedbackSubmitted(params: {
    userId?: string | null;
    feedbackType?: string | null;
    pagePath?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'ops',
      eventName: 'feedback_submitted',
      userId: params.userId || null,
      source: 'feedback_form',
      pagePath: params.pagePath || null,
      properties: {
        feedbackType: params.feedbackType || null
      }
    });
  }

  static async trackContactMessageSubmitted(params: {
    userId?: string | null;
    pagePath?: string | null;
    subject?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'ops',
      eventName: 'contact_message_submitted',
      userId: params.userId || null,
      source: 'contact_form',
      pagePath: params.pagePath || null,
      properties: {
        subject: params.subject || null
      }
    });
  }

  static async trackBlogCtaClick(params: {
    postSlug?: string | null;
    locale?: 'it' | 'en' | null;
    context: 'archive' | 'post';
    target: 'platform' | 'landing' | 'newsletter';
    source?: string | null;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'blog',
      eventName: 'blog_cta_clicked',
      locale: params.locale || null,
      source: params.source || `blog_${params.context}`,
      properties: {
        postSlug: params.postSlug || null,
        context: params.context,
        target: params.target
      }
    });
  }

  static async trackNewsletterFormStarted(params: {
    postSlug?: string | null;
    locale?: 'it' | 'en' | null;
    source: string;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'newsletter',
      eventName: 'newsletter_form_started',
      locale: params.locale || null,
      source: params.source,
      properties: {
        postSlug: params.postSlug || null
      }
    });
  }

  static async trackNewsletterSubscribed(params: {
    postSlug?: string | null;
    locale?: 'it' | 'en' | null;
    source: string;
    isTrader: boolean;
  }): Promise<void> {
    await this.captureEvent({
      domain: 'newsletter',
      eventName: 'newsletter_subscribed',
      locale: params.locale || null,
      source: params.source,
      properties: {
        postSlug: params.postSlug || null,
        isTrader: params.isTrader
      }
    });
  }

  static async trackPaymentEvent(eventName: 'payment_pricing_viewed' | 'payment_plan_selected' | 'payment_checkout_started' | 'payment_checkout_completed' | 'payment_checkout_failed', properties: Record<string, unknown> = {}): Promise<void> {
    await this.captureEvent({
      domain: 'payments',
      eventName,
      source: 'payments_placeholder',
      properties
    });
  }

  /**
   * The connect-a-platform funnel: started → connected → first trades, plus
   * the failures in between. One name per stage and `platform` in the
   * properties, so a launch can be read per platform and per stage — and a
   * `plan_limit_files` failure is a Freemium trader who wanted a second
   * account, which is the upgrade the campaign is asking for.
   */
  static async trackPlatformEvent(
    eventName: 'platform_connect_started' | 'platform_connected' | 'platform_trades_received' | 'platform_connect_failed',
    properties: { platform: 'mt5' | 'mt4' | 'ctrader' } & Record<string, unknown>,
  ): Promise<void> {
    await this.captureEvent({
      domain: 'product',
      eventName,
      source: 'platforms',
      properties,
    });
  }

  static async trackOpsEvent(
    eventName: 'agent_run_started' | 'agent_run_completed' | 'agent_run_failed' | 'feature_used',
    properties: Record<string, unknown> = {},
    userId?: string | null
  ): Promise<void> {
    await this.captureEvent({
      domain: 'ops',
      eventName,
      userId: userId || null,
      source: 'app_ops',
      properties
    });
  }
}
